import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Typography,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  ElectricBolt as ElectricityIcon,
  Water as WaterIcon,
  LocalGasStation as GasIcon,
  Wifi as InternetIcon,
  Build as MaintenanceIcon,
  MonetizationOn as InvoiceIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import billService from '../services/billService';
import tenantService from '../services/tenantService';
import { PageHeader, Loading, EmptyState } from '../components/Layout';

const Bills = () => {
  const [bills, setBills] = useState([]);
  const [totalBills, setTotalBills] = useState(0);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [formData, setFormData] = useState({
    tenant_id: '',
    room_id: '',
    bill_type: 'electricity',
    amount: '',
    billing_period: new Date().toISOString().slice(0, 7), // YYYY-MM
    due_date: '',
  });
  const [filters, setFilters] = useState({
    tenant_id: '',
    bill_type: '',
    status: '',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    console.log('Bills component mounted - fetching data...');
    fetchBills();
    fetchTenants();
  }, [page, rowsPerPage, filters]);

  const fetchBills = async () => {
    try {
      setLoading(true);
      console.log('Fetching bills from API...');
      
      // Build query params for server-side pagination and filtering
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      };
      
      if (filters.tenant_id) params.tenant_id = filters.tenant_id;
      if (filters.bill_type) params.bill_type = filters.bill_type;
      if (filters.status) params.status = filters.status;
      
      const response = await billService.getAllBills(params);
      console.log('Bills API response:', response);
      
      // Backend now returns {items: [], total: N, skip: N, limit: N}
      const { items, total } = response.data;
      console.log('Bills data:', items);
      console.log('Total bills:', total);
      
      setBills(Array.isArray(items) ? items : []);
      setTotalBills(total || 0);
      
      if (Array.isArray(items) && items.length > 0) {
        console.log('Sample bill:', items[0]);
      }
    } catch (error) {
      console.error('Error fetching bills:', error);
      console.error('Error response:', error.response);
      if (error.response?.status !== 404) {
        toast.error('Failed to load bills');
      }
      setBills([]);
      setTotalBills(0);
    } finally {
      setLoading(false);
    }
  };


  const fetchTenants = async () => {
    try {
      console.log('Fetching tenants...');
      const response = await tenantService.getAllTenants();
      console.log('Tenants API response:', response);
      console.log('Tenants data:', response.data);
      console.log('All tenants count:', response.data?.length || 0);
      
      const activeTenants = response.data.filter(t => t.is_active) || [];
      console.log('Active tenants count:', activeTenants.length);
      console.log('Active tenants:', activeTenants);
      
      if (activeTenants.length > 0) {
        console.log('Sample tenant structure:', activeTenants[0]);
      } else {
        console.warn('No active tenants found!');
      }
      
      setTenants(activeTenants);
    } catch (error) {
      console.error('Failed to load tenants:', error);
      console.error('Error details:', error.response);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      tenant_id: '',
      room_id: '',
      bill_type: 'electricity',
      amount: '',
      billing_period: new Date().toISOString().slice(0, 7),
      due_date: '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleTenantChange = (e) => {
    const tenantId = e.target.value;
    console.log('Tenant changed to:', tenantId);
    const tenant = tenants.find(t => t.id === tenantId);
    console.log('Found tenant:', tenant);
    console.log('Tenant current_room_id:', tenant?.current_room_id);
    setFormData({
      ...formData,
      tenant_id: tenantId,
      room_id: tenant?.current_room_id || '',
    });
    console.log('Updated formData will be:', {
      ...formData,
      tenant_id: tenantId,
      room_id: tenant?.current_room_id || '',
    });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCreateBill = async () => {
    console.log('handleCreateBill called');
    console.log('formData:', formData);
    console.log('Validation check:');
    console.log('  tenant_id:', formData.tenant_id, '| Type:', typeof formData.tenant_id);
    console.log('  room_id:', formData.room_id, '| Type:', typeof formData.room_id);
    console.log('  amount:', formData.amount, '| Type:', typeof formData.amount);
    console.log('  due_date:', formData.due_date, '| Type:', typeof formData.due_date);
    
    // Validate required fields (room_id is optional if tenant doesn't have a room yet)
    if (!formData.tenant_id || !formData.amount || !formData.due_date) {
      console.error('Validation failed!');
      console.error('Missing fields:', {
        tenant_id: !formData.tenant_id,
        amount: !formData.amount,
        due_date: !formData.due_date
      });
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Check if tenant has a room assigned
    if (!formData.room_id) {
      console.warn('Tenant does not have a room assigned');
      toast.warning('Note: This tenant does not have a room assigned');
    }
    
    try {
      const billData = {
        tenant_id: parseInt(formData.tenant_id),
        room_id: formData.room_id ? parseInt(formData.room_id) : null,
        bill_type: formData.bill_type,
        amount: parseFloat(formData.amount),
        billing_period: formData.billing_period,
        due_date: formData.due_date,
      };
      
      console.log('Submitting bill data:', billData);
      await billService.createBill(billData);
      toast.success('Bill created successfully');
      handleCloseDialog();
      fetchBills();
    } catch (error) {
      console.error('Error creating bill:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to create bill');
    }
  };

  const handleOpenEditDialog = (bill) => {
    setSelectedBill(bill);
    setEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialog(false);
    setSelectedBill(null);
  };

  const handleUpdateBill = async () => {
    try {
      const updateData = {
        amount: selectedBill.amount,
        due_date: selectedBill.due_date,
        status: selectedBill.status,
      };
      await billService.updateBill(selectedBill.id, updateData);
      toast.success('Bill updated successfully');
      handleCloseEditDialog();
      fetchBills();
    } catch (error) {
      toast.error('Failed to update bill');
      console.error(error);
    }
  };

  const handleOpenDeleteDialog = (bill) => {
    setSelectedBill(bill);
    setDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog(false);
    setSelectedBill(null);
  };

  const handleDeleteBill = async () => {
    try {
      await billService.deleteBill(selectedBill.id);
      toast.success('Bill deleted successfully');
      handleCloseDeleteDialog();
      fetchBills();
    } catch (error) {
      toast.error('Failed to delete bill');
      console.error(error);
    }
  };

  const handleConvertToInvoice = async (bill) => {
    if (bill.invoice_id) {
      toast.info('This bill has already been converted to an invoice');
      return;
    }
    
    if (bill.status === 'paid') {
      toast.error('Paid bills cannot be converted to invoices');
      return;
    }
    
    try {
      const response = await billService.convertToInvoice(bill.id);
      toast.success(`Bill converted to invoice ${response.data.invoice_number}`);
      fetchBills();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert bill to invoice');
      console.error(error);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
    setPage(0); // Reset to first page when filtering
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getBillTypeIcon = (type) => {
    switch (type) {
      case 'electricity':
        return <ElectricityIcon fontSize="small" />;
      case 'water':
        return <WaterIcon fontSize="small" />;
      case 'gas':
        return <GasIcon fontSize="small" />;
      case 'internet':
        return <InternetIcon fontSize="small" />;
      case 'maintenance':
        return <MaintenanceIcon fontSize="small" />;
      default:
        return <ReceiptIcon fontSize="small" />;
    }
  };

  const getBillTypeColor = (type) => {
    switch (type) {
      case 'electricity':
        return 'warning';
      case 'water':
        return 'info';
      case 'gas':
        return 'error';
      case 'internet':
        return 'secondary';
      case 'maintenance':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'partial_paid':
        return 'info';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  // Filtering is now done server-side
  // Calculate statistics from current page data
  // Note: These are partial stats. For complete stats, use the statistics endpoint
  console.log('=== Bills Statistics Calculation ===#');
  console.log('Total bills in system:', totalBills);
  console.log('Current page bills:', bills.length);
  
  if (bills.length > 0) {
    console.log('Sample bill structure:', bills[0]);
  }
  
  const pendingBills = bills.filter(b => b.status === 'pending').length;
  const pendingAmount = bills.filter(b => b.status === 'pending').reduce((sum, b) => sum + (b.amount || 0), 0);
  const paidBills = bills.filter(b => b.status === 'paid').length;
  const paidAmount = bills.filter(b => b.status === 'paid').reduce((sum, b) => sum + (b.amount || 0), 0);
  const overdueBills = bills.filter(b => b.status === 'overdue').length;
  const overdueAmount = bills.filter(b => b.status === 'overdue').reduce((sum, b) => sum + (b.amount || 0), 0);
  
  const partialPaidBills = bills.filter(b => b.status === 'partial_paid');
  const partialPaidBillsCount = partialPaidBills.length;
  
  const partialPaidRemaining = partialPaidBills
    .reduce((sum, b) => {
      const remaining = typeof b.remaining_balance === 'number' 
        ? b.remaining_balance 
        : Math.max((b.amount || 0) - (b.paid_amount || 0), 0);
      return sum + remaining;
    }, 0);
  const totalPendingCount = pendingBills + partialPaidBillsCount;
  const totalPendingAmount = pendingAmount + partialPaidRemaining;

  if (loading) {
    return <Loading message="Loading bills..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Utility Bills"
        subtitle="Manage utility bills for tenants"
        breadcrumbs={[{ label: 'Bills' }]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchBills}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
            >
              Create Bill
            </Button>
          </Box>
        }
      />

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Pending Bills
              </Typography>
              <Typography variant="h4">{pendingBills}</Typography>
              <Typography variant="h6" color="textSecondary" fontWeight="medium">
                KES {pendingAmount.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
            <CardContent>
              <Typography color="inherit" variant="body2" gutterBottom>
                Partial Paid
              </Typography>
              <Typography variant="h4" color="inherit">{partialPaidBillsCount}</Typography>
              <Typography variant="h6" color="inherit" fontWeight="medium">
                KES {partialPaidRemaining.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Overdue Bills
              </Typography>
              <Typography variant="h4">{overdueBills}</Typography>
              <Typography variant="h6" color="textSecondary" fontWeight="medium">
                KES {overdueAmount.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Paid Bills
              </Typography>
              <Typography variant="h4">{paidBills}</Typography>
              <Typography variant="h6" color="textSecondary" fontWeight="medium">
                KES {paidAmount.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <CardContent>
              <Typography color="inherit" variant="body2" gutterBottom>
                Total Pending Amount
              </Typography>
              <Typography variant="h4" color="inherit">
                {totalPendingCount}
              </Typography>
              <Typography variant="h6" color="inherit" fontWeight="medium">
                KES {totalPendingAmount.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              select
              label="Filter by Tenant"
              name="tenant_id"
              value={filters.tenant_id}
              onChange={handleFilterChange}
              size="small"
            >
              <MenuItem value="">All Tenants</MenuItem>
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.full_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              select
              label="Filter by Type"
              name="bill_type"
              value={filters.bill_type}
              onChange={handleFilterChange}
              size="small"
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="electricity">Electricity</MenuItem>
              <MenuItem value="water">Water</MenuItem>
              <MenuItem value="gas">Gas</MenuItem>
              <MenuItem value="internet">Internet</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              select
              label="Filter by Status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              size="small"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="partial_paid">Partial Paid</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Bills Table */}
      {totalBills === 0 ? (
        <EmptyState
          icon={ReceiptIcon}
          title="No Bills Yet"
          description="Create utility bills for your tenants to start tracking."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
            >
              Create First Bill
            </Button>
          }
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Tenant</strong></TableCell>
                <TableCell><strong>Room</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Amount (KES)</strong></TableCell>
                <TableCell><strong>Period</strong></TableCell>
                <TableCell><strong>Due Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Invoice No</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bills.map((bill) => (
                <TableRow key={bill.id}>
                  <TableCell>{bill.tenant_name}</TableCell>
                  <TableCell>{bill.room_number}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getBillTypeIcon(bill.bill_type)}
                      label={bill.bill_type.toUpperCase()}
                      color={getBillTypeColor(bill.bill_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{bill.amount.toLocaleString()}</TableCell>
                  <TableCell>{bill.billing_period}</TableCell>
                  <TableCell>
                    {new Date(bill.due_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bill.status.toUpperCase()}
                      color={getStatusColor(bill.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {bill.invoice_number ? (
                      <Typography variant="body2" color="success.main" fontWeight="medium">
                        #{bill.invoice_number}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {!bill.invoice_id && bill.status !== 'paid' && (
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleConvertToInvoice(bill)}
                        title="Convert to Invoice"
                      >
                        <InvoiceIcon />
                      </IconButton>
                    )}
                    {bill.invoice_id && (
                      <Chip
                        label="Invoiced"
                        size="small"
                        color="success"
                        sx={{ mr: 1 }}
                      />
                    )}
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenEditDialog(bill)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleOpenDeleteDialog(bill)}
                      disabled={!!bill.invoice_id}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}</TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
            component="div"
            count={totalBills}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Create Bill Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Bill</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label="Select Tenant"
            name="tenant_id"
            value={formData.tenant_id}
            onChange={handleTenantChange}
            margin="normal"
            required
          >
            {tenants.map((tenant) => (
              <MenuItem key={tenant.id} value={tenant.id}>
                {tenant.full_name} - Room {tenant.room_number}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            select
            label="Bill Type"
            name="bill_type"
            value={formData.bill_type}
            onChange={handleInputChange}
            margin="normal"
            required
          >
            <MenuItem value="electricity">Electricity</MenuItem>
            <MenuItem value="water">Water</MenuItem>
            <MenuItem value="gas">Gas</MenuItem>
            <MenuItem value="internet">Internet</MenuItem>
            <MenuItem value="maintenance">Maintenance</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Amount (KES)"
            name="amount"
            type="number"
            value={formData.amount}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Billing Period"
            name="billing_period"
            type="month"
            value={formData.billing_period}
            onChange={handleInputChange}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            fullWidth
            label="Due Date"
            name="due_date"
            type="date"
            value={formData.due_date}
            onChange={handleInputChange}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleCreateBill} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Bill Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Bill</DialogTitle>
        <DialogContent>
          {selectedBill && (
            <>
              <TextField
                fullWidth
                label="Amount (KES)"
                type="number"
                value={selectedBill.amount}
                onChange={(e) => setSelectedBill({...selectedBill, amount: parseFloat(e.target.value)})}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={selectedBill.due_date}
                onChange={(e) => setSelectedBill({...selectedBill, due_date: e.target.value})}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                select
                label="Status"
                value={selectedBill.status}
                onChange={(e) => setSelectedBill({...selectedBill, status: e.target.value})}
                margin="normal"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="partial_paid">Partial Paid</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
              </TextField>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleUpdateBill} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Bill</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this bill? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteBill} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Bills;
